import React, { useEffect, useRef, useState } from "react";
import { GripVertical, Image as ImageIcon, Loader2, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const logoUrl = (id, updatedAt) => `${API}/public/partner-logos/${id}?v=${encodeURIComponent(updatedAt || "1")}`;

const inspectPng = (file, previewUrl) => new Promise((resolve) => {
    const image = new window.Image();
    image.onload = () => {
        const scale = Math.min(1, 320 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let transparent = false;
        for (let index = 3; index < pixels.length; index += 4) {
            if (pixels[index] < 250) { transparent = true; break; }
        }
        resolve({ previewUrl, opaque: !transparent });
    };
    image.onerror = () => resolve({ previewUrl, opaque: true });
    image.src = previewUrl;
});

const inspectSvg = async (file) => {
    const text = await file.text();
    const documentSvg = new DOMParser().parseFromString(text, "image/svg+xml");
    if (documentSvg.querySelector("parsererror") || documentSvg.documentElement.tagName.toLowerCase() !== "svg") throw new Error("SVG non valido");
    documentSvg.querySelectorAll("script, foreignObject, iframe, object, embed, link").forEach((node) => node.remove());
    documentSvg.querySelectorAll("*").forEach((node) => [...node.attributes].forEach((attribute) => {
        if (attribute.name.toLowerCase().startsWith("on")) node.removeAttribute(attribute.name);
        if (["href", "xlink:href", "src"].includes(attribute.name.toLowerCase()) && !attribute.value.startsWith("#")) node.removeAttribute(attribute.name);
    }));
    const opaque = [...documentSvg.querySelectorAll("rect")].some((rect) => {
        const fill = (rect.getAttribute("fill") || "").toLowerCase();
        return fill && !["none", "transparent"].includes(fill) && (rect.getAttribute("width") === "100%" || rect.getAttribute("height") === "100%");
    });
    const sanitized = new XMLSerializer().serializeToString(documentSvg);
    return { previewUrl: URL.createObjectURL(new Blob([sanitized], { type: "image/svg+xml" })), opaque };
};

export const AdminPartnerLogos = () => {
    const [items, setItems] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [draft, setDraft] = useState(null);
    const [draftAgencyId, setDraftAgencyId] = useState("");
    const [busy, setBusy] = useState(false);
    const [dragged, setDragged] = useState(null);
    const inputRef = useRef(null);

    const load = async () => {
        const [logos, agencyProfiles] = await Promise.all([
            api.get("/admin/partner-logos"),
            api.get("/admin/partner-logos/agencies"),
        ]);
        setItems(logos.data.items || []);
        setAgencies(agencyProfiles.data.items || []);
    };
    useEffect(() => { load().catch(() => toast.error("Loghi partner non disponibili")); }, []);
    useEffect(() => () => { if (draft?.previewUrl) URL.revokeObjectURL(draft.previewUrl); }, [draft]);

    const chooseFile = async (file) => {
        if (!file) return;
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (!["png", "svg"].includes(ext)) { toast.error("Formato non supportato: usa esclusivamente PNG o SVG"); return; }
        if (file.size > 2 * 1024 * 1024) { toast.error("Il logo non può superare 2 MB"); return; }
        try {
            const previewUrl = URL.createObjectURL(file);
            const analysis = ext === "png" ? await inspectPng(file, previewUrl) : await inspectSvg(file);
            if (ext === "svg") URL.revokeObjectURL(previewUrl);
            setDraft({ file, ...analysis });
        } catch (error) {
            toast.error(error.message || "Logo non valido");
        } finally { if (inputRef.current) inputRef.current.value = ""; }
    };

    const upload = async () => {
        if (!draft?.file) return;
        setBusy(true);
        try {
            const body = new FormData();
            body.append("file", draft.file);
            if (draftAgencyId) body.append("agency_id", draftAgencyId);
            const { data } = await api.post("/admin/partner-logos", body);
            if (data.opaque_background) toast.warning("Consigliamo uno sfondo trasparente per una migliore resa");
            URL.revokeObjectURL(draft.previewUrl);
            setDraft(null);
            setDraftAgencyId("");
            await load();
            toast.success("Logo partner salvato");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Caricamento non riuscito");
        } finally { setBusy(false); }
    };

    const linkAgency = async (logoId, agencyId) => {
        setBusy(`agency-${logoId}`);
        try {
            const { data } = await api.put(`/admin/partner-logos/${logoId}/agency`, { agency_id: agencyId || null });
            setItems(data.items || []);
            toast.success(agencyId ? "Agenzia collegata" : "Collegamento rimosso");
        } catch (error) {
            toast.error(error?.response?.data?.detail || "Collegamento non salvato");
        } finally { setBusy(false); }
    };

    const reorder = async (targetId) => {
        if (!dragged || dragged === targetId) return;
        const next = [...items];
        const from = next.findIndex((item) => item.id === dragged);
        const to = next.findIndex((item) => item.id === targetId);
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setItems(next);
        setDragged(null);
        try { await api.put("/admin/partner-logos/reorder", { ids: next.map((item) => item.id) }); }
        catch { toast.error("Riordino non salvato"); await load(); }
    };

    const remove = async (item) => {
        if (!window.confirm(`Eliminare il logo “${item.name}”?`)) return;
        await api.delete(`/admin/partner-logos/${item.id}`);
        await load();
        toast.success("Logo eliminato");
    };

    return (
        <div data-testid="admin-partner-logos-tab" className="space-y-8">
            <div><span className="label-eyebrow text-gold">Homepage pubblica</span><h2 className="font-serif text-3xl text-ink-50 mt-2">Agenzie Partner</h2><p className="text-sm text-ink-400 mt-3">Carica PNG o SVG. I logo vengono normalizzati a 90 px di altezza e mostrati nell’ordine scelto.</p></div>
            <section className="border border-white/10 bg-ink-900/35 p-5 sm:p-6">
                <input ref={inputRef} data-testid="partner-logo-file-input" type="file" accept="image/png,image/svg+xml,.png,.svg" className="hidden" onChange={(event) => chooseFile(event.target.files?.[0])} />
                {!draft ? (
                    <button data-testid="partner-logo-choose-file" type="button" onClick={() => inputRef.current?.click()} className="cta-gold px-5 py-3 text-sm flex items-center gap-2"><Upload size={15} /> Seleziona logo PNG o SVG</button>
                ) : (
                    <div data-testid="partner-logo-draft" className="space-y-4">
                        <div className="flex justify-between gap-4"><div><div className="text-sm text-ink-50">Anteprima carousel</div><div className="text-xs text-ink-400 mt-1">{draft.file.name}</div></div><button data-testid="partner-logo-draft-close" type="button" onClick={() => { setDraft(null); setDraftAgencyId(""); }} aria-label="Rimuovi anteprima" className="icon-btn"><X size={15} /></button></div>
                        <div data-testid="partner-logo-preview" className="h-[100px] overflow-hidden flex items-center bg-transparent"><img src={draft.previewUrl} alt="Anteprima logo partner" className="h-[90px] w-auto object-contain" /></div>
                        {draft.opaque && <div data-testid="partner-logo-background-warning" className="text-xs text-amber-300">Consigliamo uno sfondo trasparente per una migliore resa</div>}
                        <label className="block max-w-xl"><span className="block text-[10px] uppercase tracking-wider text-ink-400 mb-1">Collega ad agenzia <span className="normal-case tracking-normal">(opzionale)</span></span><select data-testid="partner-logo-draft-agency" value={draftAgencyId} onChange={(event) => setDraftAgencyId(event.target.value)} className="vd-input w-full"><option value="">Nessuna agenzia</option>{agencies.map((agency) => <option key={agency.id} value={agency.id}>{agency.name} · {agency.email}</option>)}</select></label>
                        <button data-testid="partner-logo-confirm-upload" type="button" onClick={upload} disabled={busy} className="cta-gold px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-40">{busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Conferma e salva</button>
                    </div>
                )}
            </section>
            <section>
                <div className="flex items-center justify-between mb-4"><h3 className="font-serif text-xl text-ink-50">Ordine carousel</h3><span data-testid="partner-logo-count" className="text-xs text-ink-400">{items.length} loghi</span></div>
                {!items.length ? <div data-testid="partner-logo-empty" className="border border-white/10 p-10 text-center text-sm text-ink-400"><ImageIcon size={24} className="mx-auto mb-3" />Nessun logo partner pubblicato.</div> : <div className="space-y-2">{items.map((item) => (
                    <div data-testid={`partner-logo-row-${item.id}`} key={item.id} draggable onDragStart={() => setDragged(item.id)} onDragEnd={() => setDragged(null)} onDragOver={(event) => event.preventDefault()} onDrop={() => reorder(item.id)} className={`vd-card p-3 flex items-center gap-4 ${dragged === item.id ? "opacity-45" : ""}`}>
                        <GripVertical size={18} className="text-ink-500 cursor-grab shrink-0" />
                        <div className="h-[90px] min-w-[180px] flex items-center overflow-hidden"><img data-testid={`partner-logo-image-${item.id}`} src={logoUrl(item.id, item.updated_at)} alt={`Logo ${item.name}`} className="h-[90px] w-auto object-contain" /></div>
                        <div className="min-w-0 flex-1"><div className="text-sm text-ink-50 truncate">{item.name}</div><div className="text-xs text-ink-400 mt-1">{item.render_width} × 90 px</div>{item.opaque_background && <div className="text-[11px] text-amber-300 mt-1">Sfondo non trasparente</div>}<label className="block mt-2"><span className="sr-only">Collega {item.name} ad agenzia</span><select data-testid={`partner-logo-agency-${item.id}`} value={item.linked_agency_id || ""} disabled={busy === `agency-${item.id}`} onChange={(event) => linkAgency(item.id, event.target.value)} className="vd-input w-full max-w-md text-xs"><option value="">Nessuna agenzia</option>{agencies.map((agency) => <option key={agency.id} value={agency.id}>{agency.name} · {agency.email}</option>)}</select></label></div>
                        <button data-testid={`partner-logo-delete-${item.id}`} type="button" onClick={() => remove(item)} aria-label={`Elimina ${item.name}`} className="p-2 text-ink-400 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                ))}</div>}
            </section>
        </div>
    );
};